import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import "../assets/css/Home.css";
import { Thumbnail } from "../components";

export default function Home({ initialModule = "All" }) {
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const [resources, setResources] = useState([]); // combined fetched data
  const [loading, setLoading] = useState(true);

  const [searchParams] = useSearchParams();
  const querySearch = searchParams.get("search") || "";

  const [search, setSearch] = useState(querySearch);
  const [moduleFilter, setModuleFilter] = useState(initialModule); // 'All', 'Collateral', 'PreSales'
  const [category, setCategory] = useState("All"); // for PreSales
  const [tag, setTag] = useState("All"); // for Collateral

  const navigate = useNavigate();

  // ✅ Sync search state with URL parameter
  useEffect(() => {
    setSearch(querySearch);
  }, [querySearch]);

  // ✅ Fetch data from API
  useEffect(() => {
    const fetchCollaterals = axios.get(`${BASE_URL}/collaterals/list-api/`);
    const fetchPreSales = axios.post(`${BASE_URL}/presales/api/list-api/`, {
      category: "All",
    });

    Promise.all([fetchCollaterals, fetchPreSales])
      .then(([collateralsRes, preSalesRes]) => {
        const cleanData = (data) =>
          data.filter((item) => item.title && item.title !== "nan");

        const collaterals = cleanData(collateralsRes.data).map((item) => ({
          ...item,
          module: "collateral",
        }));

        const preSalesData = preSalesRes.data;
        const preSales = [
          ...cleanData(preSalesData.pre_case_studies),
          ...cleanData(preSalesData.domain_capabilities),
          ...cleanData(preSalesData.service_capabilities),
        ].map((item) => ({
          ...item,
          module: "presales",
        }));

        const combined = [...collaterals, ...preSales];
        combined.sort((a, b) => (a.title || "").localeCompare(b.title || "", undefined, { sensitivity: 'base' }));

        setResources(combined);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching data:", err);
        setLoading(false);
      });
  }, []);

  // Filter logic
  const filtered = useMemo(() => {
    return resources.filter((p) => {
      // Module Filter
      if (moduleFilter !== "All" && p.module.toLowerCase() !== moduleFilter.toLowerCase()) return false;

      // Tag Filter (for Collateral)
      if (tag !== "All") {
        if (p.module !== "collateral" || p.tag !== tag) return false;
      }

      // Category Filter (for PreSales)
      if (category !== "All") {
        if (p.module !== "presales" || p.category !== category) return false;
      }

      // Search Filter
      if (search) {
        const normalize = (str) =>
          (str || "").toLowerCase().replace(/[^a-z0-9\s]/g, "");

        const combinedText = normalize(`
          ${p.title} ${p.category || ""} ${p.company || ""}
          ${p.meta_keywords || ""} ${p.tag || ""} ${p.customer_name || ""}
          ${p.industry || ""} ${p.service_area || ""} ${p.domain || ""}
          ${p.service || ""} ${p.area || ""} ${p.keywords || ""}
        `);

        const searchWords = normalize(search).trim().split(/\s+/);
        const match = searchWords.every((word) => combinedText.includes(word));
        if (!match) return false;
      }

      return true;
    });
  }, [resources, search, tag, category, moduleFilter]);

  const allTags = useMemo(() => {
    const seen = new Set();
    const unique = [];
    resources
      .filter((r) => r.module === "collateral")
      .forEach((c) => {
        const t = String(c?.tag ?? "").trim();
        if (!t || t.toLowerCase() === "nan") return;
        if (!seen.has(t)) {
          seen.add(t);
          unique.push(t);
        }
      });
    unique.sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base", numeric: true })
    );
    return ["All", ...unique];
  }, [resources]);

  const allCategories = useMemo(() => {
    const seen = new Set();
    const unique = [];
    resources
      .filter((r) => r.module === "presales")
      .forEach((c) => {
        const cat = String(c?.category ?? "").trim();
        if (!cat || cat.toLowerCase() === "nan") return;
        if (!seen.has(cat)) {
          seen.add(cat);
          unique.push(cat);
        }
      });
    unique.sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base", numeric: true })
    );
    return ["All", ...unique];
  }, [resources]);

  const itemsPerPage = 9;
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const paginatedData = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [filtered]);

  const handleCardClick = (item) => {
    if (item.module === "collateral") {
      navigate(
        `/collateral/${item.tag}/${item.title
          ?.toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/(^-|-$)/g, "")}`
      );
    } else {
      navigate(`/presales/${item.category}/${item.id}`);
    }
  };

  return (
    <div className="home-page">
      <section className="data-sec">
        <div className="container">
          <div className="row">
            {/* Sidebar Filters */}
            <div className="col-xl-3 col-lg-3">
              <div className="filter-sidebar">
                <h5 className="mb-3">Resource Type</h5>
                {["All", "Collateral", "PreSales"].map((m) => (
                  <p
                    key={m}
                    className={`filter-item ${moduleFilter === m ? "bg-tag-selected" : "bg-tag"
                      }`}
                    onClick={() => {
                      setModuleFilter(m);
                      setTag("All");
                      setCategory("All");
                    }}
                  >
                    {m}
                  </p>
                ))}

                {(moduleFilter === "All" || moduleFilter === "Collateral") && (
                  <>
                    <h5 className="mt-4 mb-3">Marketing</h5>
                    <div className="tag-cloud">
                      {allTags.map((t) => (
                        <span
                          key={t}
                          className={`badge me-2 mb-2 ${tag === t ? "bg-tag-selected" : "bg-tag"
                            }`}
                          onClick={() => {
                            setTag(t);
                            setModuleFilter("Collateral");
                            setCategory("All");
                          }}
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                  </>
                )}

                {(moduleFilter === "All" || moduleFilter === "PreSales") && (
                  <>
                    <h5 className="mt-4 mb-3">Presales</h5>
                    {allCategories.map((cat) => (
                      <p
                        key={cat}
                        className={`filter-item ${category === cat ? "bg-tag-selected" : "bg-tag"
                          }`}
                        onClick={() => {
                          setCategory(cat);
                          setModuleFilter("PreSales");
                          setTag("All");
                        }}
                      >
                        {cat === "pre_case_studies"
                          ? "Presales Case Study"
                          : cat === "domain_capabilities"
                            ? "Domain Capability"
                            : cat === "service_capabilities"
                              ? "Service Capability"
                              : "All"}
                      </p>
                    ))}
                  </>
                )}
              </div>
            </div>

            {/* Grid and Pagination */}
            <div className="col-xl-9 col-lg-9">
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : (
                <div id="featured" className="row g-4">
                  {filtered.length === 0 ? (
                    <div className="col-12 text-center py-4">
                      <div className="alert alert-secondary">
                        No results found. Try changing filters.
                      </div>
                    </div>
                  ) : (
                    paginatedData.map((item, i) => (
                      <div
                        className="col-12 col-md-6 col-lg-4"
                        key={item.id || i}
                        onClick={() => handleCardClick(item)}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="card h-100 shadow-sm border-0 rounded-3">
                          <Thumbnail
                            driveId={item.drive_id}
                            itemId={item.item_id}
                            title={item.title}
                          />
                          <div className="card-body">
                            <p className="card-meta">
                              {item.module === "collateral"
                                ? item.category
                                : item.category === "pre_case_studies"
                                  ? `${item.customer_name} | ${item.industry}`
                                  : item.category === "domain_capabilities"
                                    ? `${item.domain} | ${item.area}`
                                    : `${item.service} | ${item.area}`}
                            </p>
                            <h5 className="card-title">{item.title}</h5>
                            <p className="card-meta">
                              {item.module === "collateral"
                                ? `${new Date(
                                  item.created_at
                                ).toLocaleDateString("en-US", {
                                  month: "short",
                                  year: "numeric",
                                })} | ${item.company !== "nan"
                                  ? item.company
                                  : "Nitor Infotech"
                                }`
                                : item.service_area}
                            </p>
                            <div className="tags">
                              {item.module === "collateral"
                                ? item.tag
                                : item.category === "pre_case_studies"
                                  ? "Presales Case Study"
                                  : item.category === "domain_capabilities"
                                    ? "Domain Capability"
                                    : "Service Capability"}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  )}

                  {totalPages > 1 && (
                    <div className="d-flex justify-content-center mt-4">
                      <nav>
                        <ul className="pagination">
                          <li
                            className={`page-item ${currentPage === 1 ? "disabled" : ""
                              }`}
                          >
                            <button
                              className="page-link pagination-action-btn"
                              onClick={() => setCurrentPage((prev) => prev - 1)}
                            >
                              <i className="fa fa-arrow-circle-left"></i>
                            </button>
                          </li>
                          {[...Array(totalPages)].map((_, index) => (
                            <li
                              key={index}
                              className={`page-item ${currentPage === index + 1 ? "active" : ""
                                }`}
                            >
                              <button
                                className="page-link page-num"
                                onClick={() => setCurrentPage(index + 1)}
                              >
                                page {index + 1}
                              </button>
                            </li>
                          ))}
                          <li
                            className={`page-item ${currentPage === totalPages ? "disabled" : ""
                              }`}
                          >
                            <button
                              className="page-link pagination-action-btn"
                              onClick={() => setCurrentPage((prev) => prev + 1)}
                            >
                              <i className="fa fa-arrow-circle-right"></i>
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
